// TODO: Consolidate this file with circuit-shared's version, in design-system/utilities or another package
import {
  MgCannabis,
  MgCapsule,
  MgCartridge,
  MgDevice,
  MgEffervescent,
  MgInhaler,
  MgOralLiquid,
  MgPastille,
  MgPessary,
  MgPowder,
  MgResin,
  MgSpray,
  MgTablets,
  MgTopical,
  MgWafer,
} from '@montugroup/icons';
import { ProductFormulationID } from '@montugroup/pms-api-contracts';
import type { SvgIcon } from '@mui/material';

/** Key of the formulation entry in a pad item's filters, as sent by the products-by-doctor filter response */
export const FORMULATION_FILTER_ID = 'select-formulation';

export const ProductFormulations = {
  ORAL_LIQUID: 'Oral Liquid',
  HERB_DRIED: 'Herb, Dried',
  CAPSULE: 'Capsule',
  PASTILLE: 'Pastille',
  DEVICE: 'Device',
  INHALATION: 'Inhalation',
  TOPICAL: 'Topical',
  SPRAY: 'Spray',
  WAFER: 'Wafer',
  TABLET_CHEWABLE: 'Tablet, Chewable',
  TABLET_EFFERVESCENT: 'Tablet, Effervescent',
  PESSARY: 'Pessary',
  CONCENTRATED_EXTRACT: 'Concentrated Extract',
  INHALANT_PRESSURIZED: 'Inhalant (pressure)',
  POWDER: 'Powder',
} as const;

export type ProductFormulation = (typeof ProductFormulations)[keyof typeof ProductFormulations];

/** Narrows a formulation name read from data, such as a pad item's filters, to one this app has an entry for. */
export const isProductFormulation = (formulation: string): formulation is ProductFormulation =>
  Object.values<string>(ProductFormulations).includes(formulation);

export interface FormulationDefn {
  /**
   * From the contract's `ProductFormulationID`, the enum pms-service reads a product's `formulation_id` with. Pad items
   * name their formulation only by filter value, but a dispense limit sends the id.
   */
  id: ProductFormulationID;
  icon: typeof SvgIcon;
  color: string;
  displayName: string;
}

export const ProductFormulationsMap: Record<ProductFormulation, FormulationDefn> = {
  [ProductFormulations.HERB_DRIED]: {
    id: ProductFormulationID.HerbDried,
    icon: MgCannabis,
    color: '#4F6B73',
    displayName: 'Flower',
  },
  [ProductFormulations.INHALATION]: {
    id: ProductFormulationID.Inhalation,
    icon: MgCartridge,
    color: '#23AFFF',
    displayName: 'Inhalation',
  },
  [ProductFormulations.CAPSULE]: {
    id: ProductFormulationID.Capsule,
    icon: MgCapsule,
    color: '#EC407A',
    displayName: 'Capsule',
  },
  [ProductFormulations.ORAL_LIQUID]: {
    id: ProductFormulationID.OralLiquid,
    icon: MgOralLiquid,
    color: '#7986CB',
    displayName: 'Oral liquid',
  },
  [ProductFormulations.PASTILLE]: {
    id: ProductFormulationID.Pastille,
    icon: MgPastille,
    color: '#FFA726',
    displayName: 'Pastille',
  },
  [ProductFormulations.TABLET_CHEWABLE]: {
    id: ProductFormulationID.TabletChewable,
    icon: MgTablets,
    color: '#ba2cbf',
    displayName: 'Chewable tablet',
  },
  [ProductFormulations.DEVICE]: {
    id: ProductFormulationID.Device,
    icon: MgDevice,
    color: '#0074C4',
    displayName: 'Device',
  },
  [ProductFormulations.SPRAY]: {
    id: ProductFormulationID.Spray,
    icon: MgSpray,
    color: '#406ac9', // todo real colour
    displayName: 'Spray',
  },
  [ProductFormulations.TOPICAL]: {
    id: ProductFormulationID.Topical,
    icon: MgTopical,
    color: '#9E9D24', // todo real colour
    displayName: 'Topical',
  },
  [ProductFormulations.WAFER]: {
    id: ProductFormulationID.Wafer,
    icon: MgWafer,
    color: '#EF6C00', // todo real colour
    displayName: 'Wafer',
  },
  [ProductFormulations.CONCENTRATED_EXTRACT]: {
    id: ProductFormulationID.ConcentratedExtract,
    icon: MgResin,
    color: '#4A148C',
    displayName: 'Concentrated Extract',
  },
  [ProductFormulations.INHALANT_PRESSURIZED]: {
    id: ProductFormulationID.InhalantPressure,
    icon: MgInhaler,
    color: '#00897B',
    displayName: 'Inhalant (pressure)',
  },
  [ProductFormulations.POWDER]: {
    id: ProductFormulationID.Powder,
    icon: MgPowder,
    color: '#AF7320',
    displayName: 'Powder',
  },
  [ProductFormulations.TABLET_EFFERVESCENT]: {
    id: ProductFormulationID.TabletEffervescent,
    icon: MgEffervescent,
    color: '#8A8F3A',
    displayName: 'Tablet, Effervescent',
  },
  [ProductFormulations.PESSARY]: {
    id: ProductFormulationID.Pessary,
    icon: MgPessary,
    color: '#C46B3C',
    displayName: 'Pessary',
  },
} as const;
